import { useMessageScrollerScrollable } from "@shadcn/react/message-scroller";
import {
    createContext,
    type ReactNode,
    use,
    useEffect,
    useEffectEvent,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import {
    getFirstUnreadMessageId,
    type MountPin,
    NEW_MESSAGES_LABEL_CLEAR_DELAY_MS,
    resolveMountPinAfterSettle,
    SCROLLED_AWAY_SETTLE_MS,
    selectDisplayedFirstUnreadMessageId,
    shouldShowUnreadJumpButton,
} from "../-utils/chatUnreadPolicy";
import { getChatScrollerViewport } from "../-utils/scrollToReplyTarget";
import { useChatPage } from "./useChatPage";

type ChatUnreadContextValue = {
    firstUnreadMessageId: string | null;
    hasUnreadMessages: boolean;
    jumpToNewMessages: () => void;
};

const ChatUnreadContext = createContext<ChatUnreadContextValue | null>(null);

export function ChatUnreadProvider({ children }: { children: ReactNode }) {
    const { scopeKey } = useChatPage();

    return <ChatUnreadProviderInner key={scopeKey}>{children}</ChatUnreadProviderInner>;
}

export function useChatUnread(): ChatUnreadContextValue {
    const value = use(ChatUnreadContext);

    if (!value) {
        throw new Error("useChatUnread must be used within ChatUnreadProvider");
    }

    return value;
}

function ChatUnreadProviderInner({ children }: { children: ReactNode }) {
    const {
        agentMessageIds,
        conversationId,
        isFetchedAfterMount,
        isFetching,
        isPending,
        markRetryToken,
        markThreadRead,
        messageIds,
        unreadMessageIds,
    } = useChatPage();

    const value = useChatUnreadMessagesState({
        agentMessageIds,
        conversationId,
        isFetchedAfterMount,
        isFetching,
        isPending,
        markRetryToken,
        markThreadRead,
        messageIds,
        unreadMessageIds,
    });

    return <ChatUnreadContext value={value}>{children}</ChatUnreadContext>;
}

function useChatUnreadMessagesState({
    agentMessageIds,
    conversationId,
    isFetchedAfterMount,
    isFetching,
    messageIds,
    isPending,
    markRetryToken,
    markThreadRead,
    unreadMessageIds,
}: {
    agentMessageIds: ReadonlySet<string>;
    conversationId: string | null;
    isFetchedAfterMount: boolean;
    isFetching: boolean;
    messageIds: readonly string[];
    isPending: boolean;
    markRetryToken: number;
    markThreadRead: (messageId: string | null) => void;
    unreadMessageIds: readonly string[];
}): ChatUnreadContextValue {
    const { end } = useMessageScrollerScrollable();
    const atBottom = !end;
    const wasScrolledAwayRef = useRef(false);

    const latestMessageId = messageIds[0] ?? null;
    const [lastReadMessageId, setLastReadMessageId] = useState<string | null>(null);
    const [mountPin, setMountPin] = useState<MountPin>({ status: "pending" });
    const [isSeeded, setIsSeeded] = useState(false);
    const [isLingeringLabel, setIsLingeringLabel] = useState(false);

    const markAllRead = useEffectEvent((messageId: string | null) => {
        setLastReadMessageId(messageId);
        markThreadRead(messageId);
    });

    const dismissMountPin = useEffectEvent(() => {
        setMountPin({ status: "closed" });
    });

    useEffect(
        function seedAfterSettledFetch() {
            if (isPending || isFetching || !isFetchedAfterMount || isSeeded) {
                return;
            }

            if (!conversationId || !latestMessageId) {
                return;
            }

            setMountPin((pin) =>
                resolveMountPinAfterSettle(pin, unreadMessageIds, messageIds),
            );
            setIsSeeded(true);
            setLastReadMessageId(latestMessageId);

            if (unreadMessageIds.length > 0) {
                markThreadRead(latestMessageId);
            }
        },
        [
            conversationId,
            isFetchedAfterMount,
            isFetching,
            isPending,
            isSeeded,
            latestMessageId,
            markThreadRead,
            messageIds,
            unreadMessageIds,
        ],
    );

    useEffect(
        function trackScrolledAwayFromBottom() {
            if (atBottom) {
                return;
            }

            const timeoutId = window.setTimeout(() => {
                wasScrolledAwayRef.current = true;
            }, SCROLLED_AWAY_SETTLE_MS);

            return () => window.clearTimeout(timeoutId);
        },
        [atBottom],
    );

    useEffect(
        function dismissMountPinWhileAtBottom() {
            if (!atBottom || !isSeeded || mountPin.status !== "pinned") {
                return;
            }

            if (wasScrolledAwayRef.current) {
                return;
            }

            const timeoutId = window.setTimeout(() => {
                if (!wasScrolledAwayRef.current) {
                    dismissMountPin();
                }
            }, NEW_MESSAGES_LABEL_CLEAR_DELAY_MS);

            return () => window.clearTimeout(timeoutId);
        },
        [atBottom, isSeeded, mountPin],
    );

    useLayoutEffect(
        function markReadWhenAtBottom() {
            if (!atBottom || !isSeeded) {
                return;
            }

            if (!wasScrolledAwayRef.current) {
                setIsLingeringLabel(false);
                markAllRead(latestMessageId);
                return;
            }

            setIsLingeringLabel(true);
            const timeoutId = window.setTimeout(() => {
                wasScrolledAwayRef.current = false;
                setIsLingeringLabel(false);
                dismissMountPin();
                markAllRead(latestMessageId);
            }, NEW_MESSAGES_LABEL_CLEAR_DELAY_MS);

            return () => window.clearTimeout(timeoutId);
        },
        [atBottom, isSeeded, latestMessageId, markRetryToken],
    );

    const firstLiveUnreadMessageId = isSeeded
        ? getFirstUnreadMessageId(messageIds, lastReadMessageId, agentMessageIds)
        : null;

    const hasLiveUnreadContent = firstLiveUnreadMessageId !== null;

    const showLiveUnreadLine = hasLiveUnreadContent && (!atBottom || isLingeringLabel);

    const firstUnreadMessageId = selectDisplayedFirstUnreadMessageId({
        showLiveUnreadLine,
        firstLiveUnreadMessageId,
        mountPin,
    });

    const hasUnreadMessages = shouldShowUnreadJumpButton({
        atBottom,
        hasLiveUnreadContent,
        mountPin,
    });

    function jumpToNewMessages() {
        const viewport = getChatScrollerViewport();
        if (!viewport) {
            return;
        }

        viewport.scrollTo({
            top: Math.max(0, viewport.scrollHeight - viewport.clientHeight),
            behavior: "smooth",
        });
    }

    return { firstUnreadMessageId, hasUnreadMessages, jumpToNewMessages };
}
